import React from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Link,
  Tooltip
} from '@mui/material';
import {
  CheckCircle,
  Cancel
} from '@mui/icons-material';

interface CollectionFieldProps {
  label: string;
  data: any;
}

// Helper function to format cell values with friendly display
const formatCellValue = (value: any): React.ReactNode => {
  // Handle null/undefined
  if (value === null || value === undefined) {
    return (
      <Typography variant="body2" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
        N/A
      </Typography>
    );
  }

  // Handle booleans with icons
  if (typeof value === 'boolean') {
    return value ? (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <CheckCircle sx={{ fontSize: 18, color: 'success.main' }} />
        <Typography variant="body2">Yes</Typography>
      </Box>
    ) : (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Cancel sx={{ fontSize: 18, color: 'error.main' }} />
        <Typography variant="body2">No</Typography>
      </Box>
    );
  }

  // Handle dates (ISO format detection)
  if (typeof value === 'string') {
    const isoDateRegex = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d{3})?Z?)?$/;
    if (isoDateRegex.test(value)) {
      try {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          const formatted = date.toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: value.includes('T') ? '2-digit' : undefined,
            minute: value.includes('T') ? '2-digit' : undefined
          });
          return (
            <Tooltip title={value}>
              <Typography variant="body2">{formatted}</Typography>
            </Tooltip>
          );
        }
      } catch (e) {
        // Not a valid date, fall through
      }
    }

    // Handle URLs
    const urlRegex = /^https?:\/\/.+/i;
    if (urlRegex.test(value)) {
      return (
        <Link href={value} target="_blank" rel="noopener noreferrer" sx={{ fontSize: '0.875rem' }}>
          {value.length > 40 ? value.substring(0, 40) + '...' : value}
        </Link>
      );
    }

    // Handle emails
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (emailRegex.test(value)) {
      return (
        <Link href={`mailto:${value}`} sx={{ fontSize: '0.875rem' }}>
          {value}
        </Link>
      );
    }

    // Handle long text with truncation
    if (value.length > 100) {
      return (
        <Tooltip title={value}>
          <Typography variant="body2" sx={{ cursor: 'help' }}>
            {value.substring(0, 100)}...
          </Typography>
        </Tooltip>
      );
    }
  }

  // Handle numbers with formatting
  if (typeof value === 'number') {
    // If it's a decimal, show 2 decimal places
    if (value % 1 !== 0) {
      return <Typography variant="body2">{value.toFixed(2)}</Typography>;
    }
    // For large integers, add comma separators
    if (value > 9999 || value < -9999) {
      return <Typography variant="body2">{value.toLocaleString()}</Typography>;
    }
  }

  // Handle objects
  if (typeof value === 'object' && value !== null) {
    const str = JSON.stringify(value);
    if (str.length > 50) {
      return (
        <Tooltip title={str}>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', cursor: 'help' }}>
            {str.substring(0, 50)}...
          </Typography>
        </Tooltip>
      );
    }
    return (
      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
        {str}
      </Typography>
    );
  }

  // Default: return as string
  return <Typography variant="body2">{String(value)}</Typography>;
};

const CollectionField: React.FC<CollectionFieldProps> = ({ label, data }) => {
  if (!data) {
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5, display: 'block' }}>
          {label}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          No data
        </Typography>
      </Box>
    );
  }

  if (!Array.isArray(data)) {
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5, display: 'block' }}>
          {label}
        </Typography>
        <Typography variant="body2" color="error" sx={{ fontStyle: 'italic' }}>
          Expected array but got {typeof data}
        </Typography>
      </Box>
    );
  }

  if (data.length === 0) {
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5, display: 'block' }}>
          {label}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          Empty list
        </Typography>
      </Box>
    );
  }

  // Check if array contains objects
  const isObjectArray = data.length > 0 && typeof data[0] === 'object' && data[0] !== null && !Array.isArray(data[0]);

  if (isObjectArray) {
    // Render as table
    const columns = Object.keys(data[0]);

    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary', mb: 1, display: 'block' }}>
          {label}
        </Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                {columns.map((col) => (
                  <TableCell key={col} sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                    {col}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((row, idx) => (
                <TableRow key={idx} hover>
                  {columns.map((col) => (
                    <TableCell key={col} sx={{ fontSize: '0.875rem' }}>
                      {formatCellValue(row[col])}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          {data.length} item{data.length !== 1 ? 's' : ''}
        </Typography>
      </Box>
    );
  } else {
    // Render as list of chips or list items
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary', mb: 1, display: 'block' }}>
          {label}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {data.map((item, idx) => (
            <Chip
              key={idx}
              label={String(item)}
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.75rem' }}
            />
          ))}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          {data.length} item{data.length !== 1 ? 's' : ''}
        </Typography>
      </Box>
    );
  }
};

export default CollectionField;
